import { useState, type ChangeEvent } from 'react';
import { Button, ButtonToggle, InputPlain } from '@peacecover/component';
import { ArrowLeft, CameraFill, Check } from '@peacecover/icon';
import { t } from 'i18next';
import { useBettieProfileImageOverlay } from '../../chat/hooks/useBettieProfileImageOverlay';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import BettieProfileImageOverlay from '../../chat/components/BettieProfileImageOverlay';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import type { AgeGapType, Gender } from '../types';

const GENDER_OPTIONS: { label: string; value: Gender | null }[] = [
  { label: t('SceneSetting.enterUserInfo.female'), value: 'FEMALE' },
  { label: t('SceneSetting.enterUserInfo.male'), value: 'MALE' },
  { label: t('SceneSetting.enterUserInfo.nonBinary'), value: null }
];
const AGE_OPTIONS: { label: string; value: AgeGapType }[] = [
  { label: t('SceneSetting.enterUserInfo.younger'), value: 'YOUNGER' },
  { label: t('SceneSetting.enterUserInfo.same'), value: 'SAME' },
  { label: t('SceneSetting.enterUserInfo.older'), value: 'OLDER' }
];

export type EnterUserInfoSubmitPayload = {
  personality?: string;
  ageGapType?: AgeGapType;
  gender?: Gender;
  nickname?: string;
  dataUrl?: string;
};

interface EnterUserInfoStepProps {
  initialNickname?: string;
  initialDataUrl?: string;
  initialGender?: Gender | '';
  initialAgeGapType?: AgeGapType | '';
  initialPersonality?: string;
  onBack: () => void;
  onSubmit: (partial: EnterUserInfoSubmitPayload) => void;
}

const EnterUserInfoStep = ({
  initialNickname = '',
  initialDataUrl = '',
  initialGender = '' as const,
  initialAgeGapType = '' as const,
  initialPersonality = '',
  onBack,
  onSubmit
}: EnterUserInfoStepProps) => {
  const [nickname, setNickname] = useState(initialNickname);
  const [dataUrl, setDataUrl] = useState(initialDataUrl);
  const [selectedGender, setSelectedGender] = useState<Gender | ''>(initialGender);
  const [selectedAge, setSelectedAge] = useState<AgeGapType | ''>(initialAgeGapType);
  const [relationship, setRelationship] = useState(initialPersonality);
  const [useMeAsNickname, setUseMeAsNickname] = useState(initialNickname === '나');
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  const { openImagePicker, fileInputProps, overlayProps } = useBettieProfileImageOverlay({
    onConfirm: ({ dataUrl: url }) => setDataUrl(url)
  });

  const handleGenderSelect = (gender: Gender) => {
    setSelectedGender(gender);
  };

  const handleAgeSelect = (age: AgeGapType) => {
    setSelectedAge(age);
  };

  const handleSubmit = () => {
    const partial: EnterUserInfoSubmitPayload = {};
    if (selectedAge) partial.ageGapType = selectedAge;
    if (selectedGender) partial.gender = selectedGender;
    const personality = relationship.trim();
    if (personality) partial.personality = personality;
    const trimmedNickname = nickname.trim();
    if (trimmedNickname) partial.nickname = trimmedNickname;
    if (dataUrl) partial.dataUrl = dataUrl;
    onSubmit(partial);
  };

  return (
    <div className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] min-h-screen flex flex-col bg-background-dark">
      <header className="px-[16px] py-[15px]">
        <button type="button" onClick={onBack}>
          <ArrowLeft className="text-pure-white" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto pb-[120px]">
        <div className="flex flex-col items-start gap-[14px] px-[24px] py-[10px]">
          <h1 className="text-[26px] font-bold text-pure-white leading-[31px] whitespace-pre-line">
            {t('SceneSetting.enterUserInfo.title')}
          </h1>
          <p className="text-[15px] font-normal text-main-light-4 leading-[18px]">
            {t('SceneSetting.enterUserInfo.description')}
          </p>
        </div>

        <div className="flex flex-col items-center gap-[24px] mt-[32px]">
          <input {...fileInputProps} />
          <div className="relative">
            <ProfileAvatar
              thumbnailUrl={dataUrl || undefined}
              widthAndHeight="82px"
              svgWidthAndHeight="56px"
              backgroundColor="#504D7F"
            />
            <button
              type="button"
              onClick={openImagePicker}
              className="bg-textPrimary-dark p-[2px] rounded-full w-fit h-fit border-[2px] border-pure-black absolute bottom-[-2px] right-[2px]"
            >
              <CameraFill size={14} className="text-pure-black" />
            </button>
          </div>
        </div>

        {/* Nickname Section */}
        <div className="mt-[24px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[16px]">
            {t('SceneSetting.enterUserInfo.name')}
          </p>
          <div className="px-[16px] py-[17px] bg-secondary-dark rounded-[12px]">
            <InputPlain
              placeholder={t('SceneSetting.enterUserInfo.placeholder') as string}
              className="w-full bg-transparent text-[17px] font-semibold !text-main-dark-2 placeholder:text-placeholder-dark"
              value={nickname}
              disabled={useMeAsNickname}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNickname(e.target.value)}
            />
          </div>
        </div>

        {/* Gender Section */}
        <div className="mt-[28px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[16px]">
            {t('SceneSetting.enterUserInfo.gender')}
          </p>
          <div className="flex gap-[6px]">
            {GENDER_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleGenderSelect(value)}
                className={`flex-1 py-[16px] rounded-[8px] text-[17px] leading-[20px] font-semibold flex items-center justify-center gap-[4px] transition-colors border
                   ${
                     selectedGender === value
                       ? 'border-onPrimary text-onPrimary'
                       : 'text-main-dark-1 border-separator-dark-1'
                   }`}
              >
                {selectedGender === value && <Check size={16} className="text-onPrimary" />}
                <span className={selectedGender === value ? 'max-w-[58px] truncate' : ''}>
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Age Section */}
        <div className="mt-[32px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[16px]">
            {t('SceneSetting.enterUserInfo.age')}
            <span className="ml-[6px] text-[13px] leading-[16px] text-main-light-4">
              {t('SceneSetting.enterUserInfo.ageDescription')}
            </span>
          </p>
          <div className="flex gap-[6px]">
            {AGE_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleAgeSelect(value)}
                className={`flex-1 py-[16px] rounded-[8px] text-[17px] leading-[20px] font-semibold flex items-center justify-center gap-[4px] transition-colors border
                   ${
                     selectedAge === value
                       ? 'border-onPrimary text-onPrimary'
                       : 'text-main-dark-1 border-separator-dark-1'
                   }`}
              >
                {selectedAge === value && <Check size={16} className="text-onPrimary" />}
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Relationship Section */}
        <div className="mt-[32px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[16px]">
            {t('SceneSetting.enterUserInfo.character')}
            <span className="ml-[6px] text-[13px] leading-[16px] text-main-light-4">
              {t('SceneSetting.enterUserInfo.characterDescription')}
            </span>
          </p>
          <div className="px-[16px] py-[17px] bg-secondary-dark rounded-[12px]">
            <InputPlain
              placeholder={t('SceneSetting.enterUserInfo.enterHere') as string}
              className="w-full bg-transparent text-[17px] font-semibold !text-main-dark-2 placeholder:text-placeholder-dark"
              value={relationship}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setRelationship(e.target.value)}
            />
          </div>
        </div>
        <button
          type="button"
          className="mt-[44px] flex items-center justify-center gap-[5px] w-full"
          onClick={() => {
            const next = !useMeAsNickname;
            setUseMeAsNickname(next);
            setNickname(next ? '나' : '');
          }}
        >
          <ButtonToggle
            size="lg"
            type="check"
            checked={useMeAsNickname}
            className="text-pure-white [--color-text-sub-lv3:currentColor] [--color-surface-lv2:rgba(255,255,255,0.20)]"
          />
          <p className="text-[15px] font-semibold text-[#FFF] leading-[18px]">
            {t('SceneSetting.enterUserInfo.setAsMe')}
          </p>
        </button>
      </div>

      {/* Footer Button */}
      <div
        className={`fixed bottom-0 left-0 right-0 px-[24px] w-full pt-[10px] pb-[calc(10px+env(safe-area-inset-bottom))] bg-background-dark transition-all duration-150 ease-in-out ${
          isKeyboardVisible
            ? 'translate-y-full opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100'
        }`}
        aria-hidden={isKeyboardVisible}
      >
        <Button
          size="large"
          className="w-full text-[17px] font-semibold disabled:bg-[#303030] disabled:text-[#4D4D50]"
          disabled={!nickname.trim()}
          onClick={handleSubmit}
        >
          {t('next')}
        </Button>
      </div>
      <BettieProfileImageOverlay {...overlayProps} />
    </div>
  );
};

export default EnterUserInfoStep;
